'use client'

import { AddressIcon } from '@repo/ui/AddressIcon'
import { AddressSelect } from '@repo/ui/AddressSelect'
import { convertAddressStringToObject } from '@repo/utils/addresses'
import { CustomDataContactsOrganisationsEditStateRow } from '@repo/custom-data-pages/CustomDataContactsOrganisationsEditState'
import { EnterIcon } from '@repo/ui/EnterIcon'
import { LoadingIcon } from '@repo/ui/LoadingIcon'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { useAddressesStore } from '@repo/store/useAddressesStore'
import { useState, useEffect } from 'react'
import MUIIconButton from '@mui/material/IconButton'
import MUIInput from '@mui/material/TextField'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const CustomDataFieldAddress = ({ value, onChange, validationErrors, sx = {} }) => {
  const addressParts = convertAddressStringToObject(value)
  const [house, setHouse] = useState(addressParts.house || '')
  const [area, setArea] = useState(addressParts.area || '')
  const [postalTown, setPostalTown] = useState(addressParts.postalTown || '')
  const [county, setCounty] = useState(addressParts.county || '')
  const [postcode, setPostcode] = useState(addressParts.postcode || '')
  const [manualAddressMode, setManualAddressMode] = useState(
    addressParts.house || addressParts.area || addressParts.postalTown || addressParts.county || addressParts.postcode
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [addressSearchTerm, setAddressSearchTerm] = useState('')
  const { isFetchingAddressSearch } = useAddressesStore()

  const toggleManualAddressMode = () => {
    setManualAddressMode(!manualAddressMode)
  }

  useEffect(() => {
    const { house, area, postalTown, county, postcode } = convertAddressStringToObject(value)

    setHouse(house || '')
    setArea(area || '')
    setPostalTown(postalTown || '')
    setCounty(county || '')
    setPostcode(postcode || '')

    setManualAddressMode(house || area || postalTown || county || postcode)
  }, [value])

  useEffect(() => {
    const addressArray = [house, area, postalTown, county, postcode]
    const addressString = addressArray.join('|')
    if (addressString !== value) onChange(addressString)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [house, area, postalTown, county, postcode])

  const handleAddressSelection = ({ house, area, postalTown, county, postcode }) => {
    setHouse(house || '')
    setArea(area || '')
    setPostalTown(postalTown || '')
    setCounty(county || '')
    setPostcode(postcode || '')
    setManualAddressMode(true)
  }

  const handlePostcodeKeyPress = (e) => {
    if (e.key === 'Enter' && !isFetchingAddressSearch) {
      e.preventDefault()
      setAddressSearchTerm(searchTerm)
    }
  }

  const hasErrors = validationErrors?.length > 0 || false

  return (
    <MUIStack
      data-testid="CustomDataFieldAddress"
      gap={1}
      sx={{
        backgroundColor: 'white',
        border: `1px solid ${hasErrors ? '#C33241' : 'rgb(0, 0, 0, 0.23)'}`,
        borderRadius: '4px',
        width: '100%',
        position: 'relative',
        padding: '47px 20px 5px 48px',
        ...sx
      }}
    >
      <CustomDataContactsOrganisationsEditStateRow
        title="Address"
        sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, width: '100%' }}
        label={
          <MUIStack direction="row" justifyContent="flex-end" alignItems="center" gap={0} sx={{ width: '100%' }}>
            <TextLinkButton
              title={manualAddressMode ? 'Search for address' : 'Enter manually'}
              onClick={toggleManualAddressMode}
              sx={{ mt: 0, minWidth: 'unset', width: 'auto', p: '0 10px' }}
            />
          </MUIStack>
        }
        isFirst
      />

      {manualAddressMode ? (
        <>
          <MUIInput
            variant="standard"
            value={house}
            onChange={(e) => setHouse(e.target.value)}
            placeholder="House"
            sx={{
              borderBottom: 'none',
              '.MuiInputBase-root': {
                marginBottom: 0,
                '&:before, &:after': { display: 'none' }
              }
            }}
          />

          <MUIInput
            variant="standard"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="Area"
            sx={{
              borderBottom: 'none',
              '.MuiInputBase-root': {
                marginBottom: 0,
                '&:before, &:after': { display: 'none' }
              }
            }}
          />

          <MUIInput
            variant="standard"
            value={postalTown}
            onChange={(e) => setPostalTown(e.target.value)}
            placeholder="Postal town"
            sx={{
              borderBottom: 'none',
              '.MuiInputBase-root': {
                marginBottom: 0,
                '&:before, &:after': { display: 'none' }
              }
            }}
          />

          <MUIInput
            variant="standard"
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            placeholder="County"
            sx={{
              borderBottom: 'none',
              '.MuiInputBase-root': {
                marginBottom: 0,
                '&:before, &:after': { display: 'none' }
              }
            }}
          />

          <MUIInput
            variant="standard"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="Post code"
            onKeyDown={handlePostcodeKeyPress}
            sx={{
              borderBottom: 'none',
              '.MuiInputBase-root': {
                marginBottom: 0,
                '&:before, &:after': { display: 'none' }
              }
            }}
            inputProps={{
              maxLength: 10
            }}
          />
        </>
      ) : null}

      {!manualAddressMode ? (
        <MUIStack justifyContent="flex-start" alignItems="center" direction="row">
          <MUIInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Enter search term"
            onKeyDown={handlePostcodeKeyPress}
            size="small"
            sx={{
              width: '100%',
              maxWidth: '600px',
              height: '40px',
              backgroundColor: 'background.dark',
              borderRadius: '6px',
              '.MuiInputBase-root': {
                borderRadius: '6px',
                height: '40px',
                mb: 0,
                paddingRight: 0,
                backgroundColor: 'background.dark'
              },
              'fieldset, .Mui-focused fieldset': {
                border: 'none'
              }
            }}
            inputProps={{
              maxLength: 200
            }}
            InputProps={{
              endAdornment: (
                <MUIInputAdornment position="end">
                  <MUIIconButton
                    onClick={() => {
                      // set it to empty string to trigger a new search
                      if (addressSearchTerm) setAddressSearchTerm('')
                      setTimeout(() => {
                        setAddressSearchTerm(searchTerm || '')
                      }, 1)
                    }}
                    disabled={!searchTerm || isFetchingAddressSearch}
                    sx={{
                      borderRadius: '0 6px 6px 0',
                      height: '40px',
                      backgroundColor: searchTerm ? 'background.slate100' : 'transparent',
                      opacity: searchTerm ? 1 : 0,
                      cursor: searchTerm ? 'pointer' : 'not-allowed',
                      transition: 'all 250ms',
                      alignItems: 'center'
                    }}
                  >
                    <MUITypography
                      variant="h6"
                      sx={{
                        mb: 0,
                        mr: 2,
                        pointerEvents: 'none',
                        width: '100%',
                        paddingLeft: '0px',
                        fontSize: '12px',
                        fontWeight: '400',
                        textTransform: 'uppercase'
                      }}
                    >
                      {isFetchingAddressSearch ? 'Loading' : 'Search'}
                    </MUITypography>
                    {isFetchingAddressSearch ? (
                      <LoadingIcon
                        sx={{
                          height: '18px',
                          width: '18px',
                          color: searchTerm ? 'white' : 'grey',
                          cursor: searchTerm ? 'pointer' : 'not-allowed',
                          transition: 'all 250ms'
                        }}
                      />
                    ) : (
                      <EnterIcon
                        sx={{
                          height: '24px',
                          width: '24px',
                          color: searchTerm ? 'white' : 'grey',
                          cursor: searchTerm ? 'pointer' : 'not-allowed',
                          transition: 'all 250ms'
                        }}
                      />
                    )}
                  </MUIIconButton>
                </MUIInputAdornment>
              )
            }}
          />
        </MUIStack>
      ) : null}

      {!manualAddressMode ? (
        <AddressSelect
          onChange={handleAddressSelection}
          size="small"
          searchTerm={addressSearchTerm}
          visible={!manualAddressMode && addressSearchTerm === searchTerm}
          sx={{ width: '100%', maxWidth: '600px', height: '40px', marginTop: '10px' }}
        />
      ) : null}

      <MUIStack
        sx={{
          width: '40px',
          height: 'calc(100% - 37px)',
          position: 'absolute',
          top: '37px',
          left: 0,
          zIndex: 1,
          backgroundColor: 'rgba(61, 81, 123, 0.04)',
          borderRadius: '4px 0 0 4px',
          borderRight: '1px solid rgb(0, 0, 0, 0.23)'
        }}
        justifyContent="center"
        alignItems="center"
      >
        <AddressIcon sx={{ opacity: 0.8, width: '20px', height: '20px' }} />
      </MUIStack>
    </MUIStack>
  )
}

CustomDataFieldAddress.propTypes = {
  field: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func,
  validationErrors: PropTypes.array,
  fieldTypeIsString: PropTypes.bool,
  fieldTypeIsNumber: PropTypes.bool,
  fieldTypeIsAnId: PropTypes.bool,
  sx: PropTypes.object
}
